/**
 * Pieces of Auto > Clients shared by the home screen and the client page:
 * the follow-up composer, transcript box, conversation summary, check-in plan,
 * permissions, form answers and timeline. Moved out of src/pages/ao/Clients.jsx
 * on 2026-10-24 when the CRM got its own home and per-person pages.
 */
import React, { useEffect, useState } from 'react';

export function when(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function firstName(name) {
  return String(name || '').trim().split(/\s+/)[0] || 'there';
}

export const REINVITE = (name) =>
  `Hi ${firstName(name)},\n\nIt looks like we missed each other. No problem at all. Things come up.\n\nIf you still want to talk, you can pick a new time here: https://calendly.com/bartpaden/a-conversation-with-bart\n\nBart`;

export const btn = 'inline-flex min-h-[44px] items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold';
export const btnRed = `${btn} bg-[#DB0812] text-white hover:opacity-90 disabled:opacity-50`;
export const btnGray = `${btn} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50`;

export function Section({ title, count, children }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">
        {title} {typeof count === 'number' ? <span className="text-gray-400">({count})</span> : null}
      </h2>
      {children}
    </section>
  );
}

export function WhatTheyWrote({ items }) {
  if (!items?.length) return <p className="text-sm text-gray-500">They did not add anything beyond the booking.</p>;
  return (
    <div className="space-y-3">
      {items.map((x) => (
        <div key={x.question}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{x.question}</p>
          <p className="whitespace-pre-wrap text-sm text-gray-900">{x.answer}</p>
        </div>
      ))}
    </div>
  );
}

export function Research({ client, onAction, busy }) {
  if (!client.research_at) {
    return <p className="text-sm text-gray-500">Research runs within 15 minutes of booking.</p>;
  }
  return (
    <div>
      {client.research_brief ? (
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-900">{client.research_brief}</div>
      ) : (
        <p className="text-sm text-amber-800">The research did not come through{client.research_error ? `: ${client.research_error}` : ''}.</p>
      )}
      <button type="button" disabled={busy} onClick={() => onAction(client, 'research')} className={`mt-3 ${btnGray}`}>
        {busy ? 'Researching… (a minute or two)' : 'Research again'}
      </button>
    </div>
  );
}

export function Notes({ client, onAction, busy }) {
  const [text, setText] = useState(client.notes || '');
  useEffect(() => setText(client.notes || ''), [client.notes]);
  return (
    <div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What you talked about, what to remember next time."
        className="min-h-[96px] w-full rounded-lg border border-gray-300 p-3 text-sm"
      />
      <button
        type="button"
        disabled={busy || text === (client.notes || '')}
        onClick={() => onAction(client, 'notes', { notes: text })}
        className={`mt-2 ${btnGray}`}
      >
        Save notes
      </button>
    </div>
  );
}

export function StagePicker({ client, stages, onAction, busy }) {
  return (
    <div className="flex flex-wrap gap-2">
      {stages.map((s) => (
        <button
          key={s.key}
          type="button"
          disabled={busy}
          onClick={() => onAction(client, 'stage', { stage: s.key })}
          className={`min-h-[36px] rounded-full px-3 text-xs font-semibold ${
            client.stage === s.key ? 'bg-gray-900 text-white' : 'border border-gray-300 bg-white text-gray-700'
          }`}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

export function FollowUp({ client, onAction, busy }) {
  const [mode, setMode] = useState('thank_you');
  const [subject, setSubject] = useState('Thank you');
  const [text, setText] = useState(client.thank_you_draft || '');
  useEffect(() => {
    if (mode === 'thank_you') {
      setSubject('Thank you');
      setText(client.thank_you_draft || '');
    } else {
      setSubject('Sorry we missed each other');
      setText(REINVITE(client.name));
    }
  }, [mode, client.thank_you_draft, client.name]);

  return (
    <div className="rounded-xl border border-[#DB0812]/30 bg-white p-4">
      <div className="mb-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => setMode('thank_you')} className={mode === 'thank_you' ? btnRed : btnGray}>
          Thank-you
        </button>
        <button type="button" onClick={() => setMode('reinvite')} className={mode === 'reinvite' ? btnRed : btnGray}>
          They did not show
        </button>
      </div>
      {mode === 'thank_you' && !client.has_transcript ? (
        <div>
          <p className="mb-2 text-sm text-gray-700">
            Add the transcript below. The thank-you is written from the real conversation: what you worked through, and the action items on both sides.
          </p>
          <button type="button" disabled={busy} onClick={() => onAction(client, 'dismiss')} className={btnGray}>
            No follow-up needed
          </button>
        </div>
      ) : (
        <div>
          <label className="mb-1 block text-xs text-gray-500">Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="mb-3 w-full rounded-lg border border-gray-300 p-2 text-sm" />
          <label className="mb-1 block text-xs text-gray-500">Message to {client.email}, from you. Replies come to you, and you are copied.</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} className="min-h-[200px] w-full rounded-lg border border-gray-300 p-3 text-sm leading-relaxed" />
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || !text.trim()}
              onClick={() => {
                if (window.confirm(`Send this to ${client.name}?`)) onAction(client, 'send', { kind: mode, subject, text });
              }}
              className={btnRed}
            >
              Send to {firstName(client.name)}
            </button>
            <button type="button" disabled={busy} onClick={() => onAction(client, 'dismiss')} className={btnGray}>
              No follow-up needed
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export function Badge({ tone = 'gray', children }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    green: 'bg-green-50 text-green-800',
    red: 'bg-red-50 text-red-800',
    amber: 'bg-amber-50 text-amber-900',
  };
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

export function Permissions({ client }) {
  const one = (label, v) => (
    <Badge tone={v === 'yes' ? 'green' : v === 'no' ? 'red' : 'gray'}>
      {label}: {v === 'yes' ? 'yes' : v === 'no' ? 'no' : 'not yet'}
    </Badge>
  );
  return (
    <div className="flex flex-wrap gap-2">
      {one('Record', client.consent?.record)}
      {one('Private Archy', client.consent?.archy)}
      {one('Check-ins', client.consent?.checkins)}
      {client.podcast_interest ? (
        <Badge tone={client.podcast_interest === 'yes' ? 'amber' : 'gray'}>
          Podcast: {client.podcast_interest === 'yes' ? 'open to it' : client.podcast_interest === 'maybe' ? 'maybe later' : 'not for them'}
        </Badge>
      ) : null}
      <Badge>
        {client.prep_submitted_at
          ? `Before we talk: filled out ${new Date(client.prep_submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
          : client.prep_email_sent_at && client.prep_email_sent_at !== 'not needed'
            ? 'Before we talk: sent, not filled out yet'
            : 'Before we talk: not sent'}
      </Badge>
    </div>
  );
}

export function PrepAnswers({ prep }) {
  if (!prep) return null;
  const rows = [
    ['Company', prep.company],
    ['Role', prep.role],
    ['Website', prep.website],
    ['LinkedIn', prep.linkedin],
    ['Texts', prep.texts_ok ? 'Text messages are fine' : null],
    ['What would make our time worth it', prep.worth_it],
    ['How they work through hard things', prep.how_you_work],
    ['Anything else', prep.anything_else],
  ].filter(([, v]) => String(v || '').trim());
  if (!rows.length) return null;
  return (
    <div className="space-y-3">
      {rows.map(([q, a]) => (
        <div key={q}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{q}</p>
          <p className="whitespace-pre-wrap text-sm text-gray-900">{a}</p>
        </div>
      ))}
    </div>
  );
}

export function TranscriptBox({ client, onAction, busy }) {
  const [text, setText] = useState('');
  if (client.consent?.record === 'no') {
    return <p className="text-sm text-gray-600">{firstName(client.name)} said no to recording, so transcripts are off for them. Use Notes instead.</p>;
  }
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setText(await file.text());
  };
  return (
    <div>
      {client.has_transcript ? (
        <p className="mb-2 text-sm text-green-700">Transcript added {new Date(client.transcript_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}. Adding another replaces the summary and plan.</p>
      ) : null}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste the transcript here, or choose a file below (.txt, .vtt, .srt, .md)."
        className="min-h-[120px] w-full rounded-lg border border-gray-300 p-3 text-sm"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input type="file" accept=".txt,.vtt,.srt,.md,text/plain" onChange={onFile} className="text-sm" />
        <button type="button" disabled={busy || text.trim().length < 200} onClick={() => onAction(client, 'transcript', { text })} className={btnRed}>
          {busy ? 'Reading the conversation… (a minute or two)' : 'Read the transcript'}
        </button>
      </div>
    </div>
  );
}

export function Analysis({ analysis, client, onAction, busy }) {
  if (!analysis) return null;
  const list = (items) => (items?.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-gray-900">{items.map((x) => <li key={x}>{x}</li>)}</ul> : <p className="text-sm text-gray-500">None.</p>);
  return (
    <div className="space-y-4 rounded-xl bg-gray-50 p-4">
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Summary (just for you)</p>
        <p className="whitespace-pre-wrap text-sm text-gray-900">{analysis.summary}</p>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Key points</p>
        {list(analysis.key_points)}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Their action items</p>
          {list(analysis.action_items?.theirs)}
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Yours</p>
          <YourItems analysis={analysis} client={client} onAction={onAction} busy={busy} />
        </div>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Worth asking next time</p>
        {list(analysis.next_time)}
      </div>
    </div>
  );
}

/**
 * Bart's action items, each with the piece of his own writing that keeps the
 * promise, when there is one. Copy puts the link on the clipboard to paste
 * into the thank-you or a note.
 */
function YourItems({ analysis, client, onAction, busy }) {
  const items = analysis.action_items?.bart || [];
  if (!items.length) return <p className="text-sm text-gray-500">None.</p>;
  const byItem = new Map((analysis.bart_links || []).map((x) => [x.item, x.links || []]));
  const copy = (url) => navigator.clipboard?.writeText(url).then(() => window.alert('Link copied.'));
  return (
    <div className="space-y-2">
      <ul className="list-disc space-y-2 pl-5 text-sm text-gray-900">
        {items.map((item) => (
          <li key={item}>
            {item}
            {(byItem.get(item) || []).map((l) => (
              <div key={l.url} className="mt-1 rounded-lg bg-white p-2 text-xs">
                <a href={l.url} target="_blank" rel="noreferrer" className="font-semibold text-blue-700">{l.title}</a>
                {l.why ? <p className="text-gray-600">{l.why}</p> : null}
                <button type="button" onClick={() => copy(l.url)} className="mt-1 font-semibold text-gray-700">Copy link</button>
              </div>
            ))}
          </li>
        ))}
      </ul>
      {analysis.bart_links_at ? null : (
        <p className="text-xs text-gray-500">
          Looking through your writing for these.{' '}
          {client && onAction ? (
            <button type="button" disabled={busy} onClick={() => onAction(client, 'writing_links')} className="font-semibold text-blue-700">Look now</button>
          ) : null}
        </p>
      )}
    </div>
  );
}

export function PlanItem({ client, item, onAction, busy }) {
  const [text, setText] = useState(item.text);
  const [date, setDate] = useState(item.send_at ? item.send_at.slice(0, 10) : '');
  useEffect(() => {
    setText(item.text);
    setDate(item.send_at ? item.send_at.slice(0, 10) : '');
  }, [item.text, item.send_at]);
  const editable = item.status === 'draft' || item.status === 'approved' || item.status === 'held';
  const changed = text !== item.text || date !== (item.send_at || '').slice(0, 10);
  const tone = { draft: 'gray', approved: 'amber', sent: 'green', stopped: 'red', held: 'red' }[item.status] || 'gray';
  const label = { draft: 'Waiting for your OK', approved: 'Approved, sends on its date', sent: 'Sent', stopped: 'Stopped', held: 'Held: they wrote to you first' }[item.status];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge tone={tone}>{label}</Badge>
        <span className="text-xs text-gray-500">{item.reason}</span>
      </div>
      <label className="mb-1 block text-xs text-gray-500">Send on</label>
      <input type="date" value={date} disabled={!editable} onChange={(e) => setDate(e.target.value)} className="mb-2 rounded-lg border border-gray-300 p-2 text-sm" />
      <textarea value={text} disabled={!editable} onChange={(e) => setText(e.target.value)} className="min-h-[120px] w-full rounded-lg border border-gray-300 p-3 text-sm leading-relaxed disabled:bg-gray-50" />
      {editable ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {changed ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onAction(client, 'plan_update', { items: [{ id: item.id, text, send_at: date ? `${date}T15:00:00Z` : item.send_at }] })}
              className={btnGray}
            >
              Save changes
            </button>
          ) : null}
          {(item.status === 'draft' || item.status === 'held') && !changed ? (
            <button type="button" disabled={busy || client.consent?.checkins === 'no'} onClick={() => onAction(client, 'plan_approve', { item_id: item.id })} className={btnRed}>
              {item.status === 'held' ? 'Send it on its date anyway' : 'Approve'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function CheckinPlan({ client, onAction, busy }) {
  const plan = client.checkin_plan || [];
  if (!plan.length) return <p className="text-sm text-gray-500">A plan is drafted after you add a transcript.</p>;
  return (
    <div className="space-y-3">
      {client.consent?.checkins === 'no' ? <p className="text-sm text-red-800">{firstName(client.name)} said no to check-ins. Nothing here will send.</p> : null}
      <p className="text-sm text-gray-600">Each one sends on its date only after you approve it. If they write to you first, the next one is held so it does not cross their note.</p>
      {plan.map((item) => (
        <PlanItem key={item.id} client={client} item={item} onAction={onAction} busy={busy} />
      ))}
      {!client.checkins_stopped_at && plan.some((x) => x.status === 'draft' || x.status === 'approved' || x.status === 'held') ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (window.confirm(`Stop all remaining check-ins for ${client.name}?`)) onAction(client, 'plan_stop');
          }}
          className={btnGray}
        >
          Stop all check-ins
        </button>
      ) : null}
    </div>
  );
}

export const EVENT_LABELS = {
  prep_email_sent: 'Before we talk form sent',
  prep_submitted: 'Filled out Before we talk',
  prep_updated: 'Updated their answers',
  transcript_added: 'Transcript added',
  thank_you_sent: 'Thank-you sent',
  reinvite_sent: 'Re-invite sent',
  checkin_sent: 'Check-in sent',
  checkins_stopped: 'Check-ins stopped',
  stage: 'Stage changed',
  reply: 'They wrote to you',
  archy_message: 'Used their private Archy',
  archy_link_sent: 'Private Archy link sent',
  archy_answer_sent: 'You answered their Archy question',
  email_out: 'You wrote to them',
  conversation: 'You talked',
};

export function Timeline({ email }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    let off = false;
    fetch(`/api/ao/clients?timeline=${encodeURIComponent(email)}`)
      .then((r) => r.json())
      .then((j) => !off && setEvents(j.ok ? j.events : []))
      .catch(() => !off && setEvents([]));
    return () => {
      off = true;
    };
  }, [email]);
  if (!events) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!events.length) return <p className="text-sm text-gray-500">Nothing yet.</p>;
  return (
    <ol className="space-y-3">
      {events.map((e) => (
        <li key={e.id} className="border-l-2 border-gray-200 pl-3">
          <p className="text-xs text-gray-500">{new Date(e.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
          <p className="text-sm font-medium text-gray-900">
            {EVENT_LABELS[e.kind] || e.kind}
            {e.kind === 'stage' && e.subject ? `: ${e.subject.replace('_', ' ')}` : ''}
            {(e.kind === 'reply' || e.kind === 'email_out' || e.kind === 'conversation') && e.subject ? `: ${e.subject}` : ''}
          </p>
          {e.body && e.kind === 'conversation' ? <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{e.body}</p> : null}
          {e.body && e.kind !== 'stage' && e.kind !== 'conversation' ? (
            <details className="text-sm text-gray-700">
              <summary className="cursor-pointer text-xs text-blue-700">Show</summary>
              <p className="mt-1 whitespace-pre-wrap">{e.body}</p>
            </details>
          ) : null}
        </li>
      ))}
    </ol>
  );
}


const CHANNEL_CHOICES = [
  ['messenger', 'Facebook Messenger'],
  ['text', 'Text'],
  ['phone', 'Phone call'],
  ['in_person', 'In person'],
  ['linkedin', 'LinkedIn message'],
  ['instagram', 'Instagram message'],
  ['email', 'Email'],
  ['other', 'Other'],
];

/**
 * Log a conversation the CRM could not see (2026-10-28): Messenger, a text,
 * a call. It lands in the person's history and counts as being in touch.
 */
export function LogConversation({ onSave, busy }) {
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
  const [channel, setChannel] = useState('messenger');
  const [when, setWhen] = useState(today);
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState('');
  const save = async () => {
    if (!note.trim()) return;
    const ok = await onSave({ channel, when, note });
    if (ok !== false) {
      setNote('');
      setSaved('Saved.');
      setTimeout(() => setSaved(''), 2500);
    }
  };
  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
      <p className="mb-2 text-sm font-semibold text-gray-900">Log a conversation</p>
      <div className="mb-2 flex flex-wrap gap-2">
        <select value={channel} onChange={(e) => setChannel(e.target.value)} className="min-h-[40px] rounded-lg border border-gray-300 bg-white px-2 text-sm" aria-label="Where">
          {CHANNEL_CHOICES.map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
        <input type="date" value={when} max={today} onChange={(e) => setWhen(e.target.value)} className="min-h-[40px] rounded-lg border border-gray-300 bg-white px-2 text-sm" aria-label="When" />
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What was said, in a sentence or two" className="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm" />
      <div className="mt-2 flex items-center gap-3">
        <button type="button" onClick={save} disabled={busy || !note.trim()} className="min-h-[40px] rounded-lg bg-gray-900 px-4 text-sm font-medium text-white disabled:opacity-40">
          Save
        </button>
        {saved ? <span className="text-sm text-green-700">{saved}</span> : null}
      </div>
    </div>
  );
}
